import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import { GenerateTaskResponse, GenerateTaskStatus } from './types'

const POLL_INTERVAL = 2500
const BATCH_SIZE = 25

export const useGenerateSuggestions = (inspectionId: number | undefined) => {
    const queryClient = useQueryClient()
    const [isGenerating, setIsGenerating] = useState(false)
    const [progress, setProgress] = useState<GenerateTaskStatus | null>(null)
    const intervalRef = useRef<number | null>(null)
    const lastRefetchedBatchRef = useRef(-1)

    const cleanup = useCallback(() => {
        if (intervalRef.current !== null) {
            window.clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    useEffect(() => cleanup, [cleanup])

    const startGenerate = useCallback(async () => {
        if (!inspectionId) return
        cleanup()
        setIsGenerating(true)
        setProgress({ state: 'PENDING' })
        lastRefetchedBatchRef.current = -1

        try {
            const res = await axiosInstance.post<GenerateTaskResponse>(
                `/workorder/${inspectionId}/action-item-suggestions/generate/`,
            )
            const { task_id } = res.data

            intervalRef.current = window.setInterval(async () => {
                try {
                    const statusRes = await axiosInstance.get<GenerateTaskStatus>(
                        `/workorder/${inspectionId}/action-item-suggestions/generate/status/`,
                        { params: { task_id } },
                    )
                    const status = statusRes.data
                    setProgress(status)

                    if (
                        status.state === 'PROGRESS' &&
                        status.current !== undefined
                    ) {
                        const currentBatch = Math.floor(
                            status.current / BATCH_SIZE,
                        )
                        if (currentBatch > lastRefetchedBatchRef.current) {
                            lastRefetchedBatchRef.current = currentBatch
                            queryClient.invalidateQueries({
                                queryKey: [
                                    'actionItemSuggestions',
                                    inspectionId,
                                ],
                            })
                        }
                    }

                    if (
                        status.state === 'SUCCESS' ||
                        status.state === 'FAILURE'
                    ) {
                        cleanup()
                        setIsGenerating(false)
                        if (status.state === 'SUCCESS') {
                            queryClient.invalidateQueries({
                                queryKey: [
                                    'actionItemSuggestions',
                                    inspectionId,
                                ],
                            })
                            const created = status.result?.created ?? 0
                            const skipped = status.result?.skipped ?? 0
                            toast.success(
                                `Generation complete — ${created} created, ${skipped} skipped`,
                            )
                        } else {
                            toast.error(status.error ?? 'Generation failed')
                        }
                    }
                } catch {
                    cleanup()
                    setProgress({
                        state: 'FAILURE',
                        error: 'Failed to check generation status',
                    })
                    setIsGenerating(false)
                }
            }, POLL_INTERVAL)
        } catch {
            setProgress({
                state: 'FAILURE',
                error: 'Failed to start generation',
            })
            setIsGenerating(false)
        }
    }, [inspectionId, cleanup, queryClient])

    const resetProgress = useCallback(() => {
        setProgress(null)
    }, [])

    return { startGenerate, isGenerating, progress, resetProgress }
}
